// src/pages/client/ClientUserManagementPage.jsx
// Client Admin user management - manage users within a specific tenant
// Updated: Added disable/enable and delete functionality

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { trackEvent, trackPageView } from "../../utils/analytics";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.invenire.app";

export default function ClientUserManagementPage({ tenantKeyFromSubdomain }) {
  const { tenantKey: tenantKeyFromUrl } = useParams();
  const tenantKey = tenantKeyFromSubdomain || tenantKeyFromUrl;
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  
  // State
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalRole, setModalRole] = useState("recruiter");
  const [saving, setSaving] = useState(false);

  // Available roles for client admin (can't assign super_admin)
  const availableRoles = ["admin", "recruiter"];

  // Set document title
  useEffect(() => {
    if (tenant) {
      document.title = `${tenant.name} - User Management`;
    }
  }, [tenant]);

  // Track page view
  useEffect(() => {
    trackPageView(`/client/${tenantKey}/users`, 'Client User Management');
  }, [tenantKey]);

  // Load tenant and users
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load tenant info - use regular fetch (public endpoint, no auth needed)
      const tenantRes = await fetch(`${API_BASE}/tenants/${tenantKey}`);
      if (tenantRes.ok) {
        const tenantData = await tenantRes.json();
        setTenant(tenantData);
        
        // Load users for this tenant - requires auth
        const usersRes = await authFetch(`${API_BASE}/auth/users?tenant_id=${tenantData.id}`);
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setUsers(usersData);
        }
      } else {
        throw new Error("Failed to load tenant");
      }
    } catch (err) {
      console.error("Failed to load data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, tenantKey]);

  useEffect(() => {
    if (tenantKey) {
      loadData();
    }
  }, [tenantKey, loadData]);

  // Filter users by search
  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      u.email?.toLowerCase().includes(query) ||
      u.name?.toLowerCase().includes(query)
    );
  });

  // Assign role
  async function handleAssignRole() {
    if (!selectedUser || !modalRole || !tenant) return;
    
    setSaving(true);
    try {
      const params = new URLSearchParams({
        role_name: modalRole,
        tenant_id: tenant.id
      });
      
      const res = await authFetch(
        `${API_BASE}/auth/users/${selectedUser.id}/roles?${params}`,
        { method: "POST" }
      );
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to assign role");
      }
      
      trackEvent("client_user_role_assigned", {
        role: modalRole,
        tenant_key: tenantKey
      });
      
      setShowRoleModal(false);
      loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Remove role (kept for future use)
  // eslint-disable-next-line no-unused-vars
  async function handleRemoveRole(userId, roleName) {
    if (!window.confirm(`Remove ${roleName} role from this user?`)) return;
    
    try {
      const url = `${API_BASE}/auth/users/${userId}/roles/${roleName}?tenant_id=${tenant.id}`;
      const res = await authFetch(url, { method: "DELETE" });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to remove role");
      }
      
      trackEvent("client_user_role_removed", { role: roleName });
      loadData();
    } catch (err) {
      alert(err.message);
    }
  }

  // Disable/Enable user
  async function handleToggleUserStatus(userToToggle) {
    const newStatus = userToToggle.is_disabled ? "enable" : "disable";
    const confirmMsg = userToToggle.is_disabled 
      ? `Enable ${userToToggle.email}? They will be able to sign in again.`
      : `Disable ${userToToggle.email}? They will not be able to sign in until re-enabled.`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const res = await authFetch(
        `${API_BASE}/auth/users/${userToToggle.id}/${newStatus}`,
        { method: "POST" }
      );
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || `Failed to ${newStatus} user`);
      }
      
      trackEvent(`client_user_${newStatus}d`, { 
        user_email: userToToggle.email,
        tenant_key: tenantKey 
      });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  // Delete user
  async function handleDeleteUser() {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      const res = await authFetch(
        `${API_BASE}/auth/users/${selectedUser.id}`,
        { method: "DELETE" }
      );
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to remove user");
      }
      
      trackEvent("client_user_deleted", { 
        user_email: selectedUser.email,
        tenant_key: tenantKey 
      });
      
      await loadData();
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Get branding colors (memoized to avoid dependency issues)
  const branding = useMemo(() => tenant?.branding || {}, [tenant?.branding]);
  const primaryColor = branding.primaryColor || branding.primary_color || "#3b82f6";
  const companyName = branding.companyName || branding.company_name || tenant?.name || tenantKey;
  
  // Get backgroundColor from branding - check both camelCase and snake_case
  const backgroundColor = branding.backgroundColor || branding.background_color || '#0d1117';
  
  // Calculate if background is light or dark for text color
  const isLightBackground = useMemo(() => {
    const hex = backgroundColor.replace('#', '');
    if (hex.length === 6) {
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5;
    }
    return false;
  }, [backgroundColor]);
  
  // Dynamic styles based on tenant branding - ALL styles are here, no static styles
  const dynamicStyles = useMemo(() => {
    const textColor = branding.textColor || (isLightBackground ? '#111827' : '#ffffff');
    const textSecondary = isLightBackground ? '#4b5563' : '#9ca3af';
    const textMuted = isLightBackground ? '#6b7280' : '#6b7280';
    const cardBg = isLightBackground ? '#ffffff' : 'rgba(255,255,255,0.03)';
    const cardBorder = isLightBackground ? '#e5e7eb' : 'rgba(255,255,255,0.1)';
    const inputBg = isLightBackground ? '#f9fafb' : 'rgba(255,255,255,0.05)';
    const inputBorder = isLightBackground ? '#d1d5db' : 'rgba(255,255,255,0.1)';
    const buttonBg = branding.buttonPrimaryBg || primaryColor;
    const buttonText = branding.buttonTextColor || '#ffffff';
    const tableHeaderBg = isLightBackground ? '#f9fafb' : 'rgba(255,255,255,0.03)';
    const tableBg = isLightBackground ? '#ffffff' : 'rgba(255,255,255,0.02)';
    const rowBorder = isLightBackground ? '#f3f4f6' : 'rgba(255,255,255,0.05)';
    const hoverBg = isLightBackground ? '#f3f4f6' : 'rgba(255,255,255,0.05)';
    const modalBg = isLightBackground ? '#ffffff' : '#1a2632';
    
    return {
      // Core layout
      container: { minHeight: "100vh", backgroundColor: backgroundColor, color: textColor, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
      header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 40px", borderBottom: `1px solid ${cardBorder}` },
      title: { fontSize: 28, fontWeight: 700, margin: 0, color: primaryColor },
      subtitle: { fontSize: 14, color: textSecondary },
      backButton: { padding: "8px 16px", background: isLightBackground ? '#f3f4f6' : "rgba(255,255,255,0.1)", color: textColor, border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer", textDecoration: "none" },
      content: { maxWidth: 1200, margin: "0 auto", padding: "40px 20px" },
      
      // Page header
      pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32, flexWrap: "wrap", gap: 16 },
      pageTitle: { fontSize: 28, marginBottom: 8, marginTop: 0, color: textColor },
      pageSubtitle: { color: textSecondary, margin: 0, fontSize: 16 },
      
      // Buttons
      button: { padding: "12px 24px", background: buttonBg, color: buttonText, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
      primaryBtn: { padding: "12px 24px", border: "none", borderRadius: 8, color: buttonText, fontSize: 14, fontWeight: 500, cursor: "pointer", background: buttonBg },
      
      // Filters
      filters: { display: "flex", gap: 12, marginBottom: 24 },
      searchInput: { flex: 1, padding: "12px 16px", background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 8, color: textColor, fontSize: 14 },
      refreshBtn: { padding: "12px 16px", background: "transparent", border: `1px solid ${cardBorder}`, borderRadius: 8, color: textSecondary, cursor: "pointer", fontSize: 14 },
      
      // Cards & inputs
      card: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: 24 },
      input: { width: "100%", padding: "12px 16px", background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 8, fontSize: 14, color: textColor, boxSizing: "border-box" },
      
      // Error & loading
      error: { display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: 8, padding: "12px 16px", marginBottom: 24, color: "#ef4444" },
      errorDismiss: { background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 },
      loading: { textAlign: "center", padding: 48, color: textSecondary },
      
      // Table
      tableContainer: { background: tableBg, borderRadius: 12, overflow: "hidden", border: `1px solid ${cardBorder}` },
      table: { width: "100%", borderCollapse: "collapse" },
      th: { textAlign: "left", padding: "14px 16px", background: tableHeaderBg, color: textSecondary, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${cardBorder}` },
      tr: { borderBottom: `1px solid ${rowBorder}` },
      td: { padding: "14px 16px", fontSize: 14, color: textColor },
      emptyCell: { padding: 48, textAlign: "center", color: textMuted },
      
      // User cell
      userCell: { display: "flex", alignItems: "center", gap: 12 },
      avatar: { width: 36, height: 36, borderRadius: "50%", objectFit: "cover" },
      avatarPlaceholder: { width: 36, height: 36, borderRadius: "50%", background: isLightBackground ? '#e5e7eb' : "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, textTransform: "uppercase", color: textColor },
      
      // Badges
      providerBadge: { padding: "4px 8px", borderRadius: 4, fontSize: 11, fontWeight: 500, textTransform: "capitalize" },
      statusBadge: { padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 500 },
      roleBadge: { display: "inline-block", padding: "2px 8px", background: isLightBackground ? "rgba(59, 130, 246, 0.1)" : "rgba(59, 130, 246, 0.2)", color: "#3b82f6", borderRadius: 4, fontSize: 11, fontWeight: 500, marginRight: 4 },
      
      // Actions
      actions: { display: "flex", gap: 6 },
      actionBtn: { padding: "6px 10px", background: hoverBg, border: `1px solid ${cardBorder}`, borderRadius: 6, color: textColor, fontSize: 12, cursor: "pointer" },
      disableBtn: { background: "rgba(251, 191, 36, 0.1)", borderColor: "rgba(251, 191, 36, 0.3)", color: "#fbbf24" },
      enableBtn: { background: "rgba(34, 197, 94, 0.1)", borderColor: "rgba(34, 197, 94, 0.3)", color: "#22c55e" },
      deleteBtn: { background: "rgba(239, 68, 68, 0.1)", borderColor: "rgba(239, 68, 68, 0.3)", color: "#ef4444" },
      
      // Stats footer
      stats: { marginTop: 16, padding: "12px 16px", background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 8, color: textSecondary, fontSize: 13 },
      
      // Modals
      modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
      modal: { background: modalBg, borderRadius: 12, padding: 24, width: "100%", maxWidth: 420, border: `1px solid ${cardBorder}` },
      modalTitle: { fontSize: 18, fontWeight: 600, marginTop: 0, marginBottom: 16, color: textColor },
      modalLabel: { display: "block", fontSize: 14, color: textSecondary, marginBottom: 8 },
      modalInput: { width: "100%", padding: "12px 16px", background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 8, color: textColor, fontSize: 14, boxSizing: "border-box" },
      
      // Role selector
      rolesGrid: { display: "flex", flexDirection: "column", gap: 8, marginTop: 16 },
      roleOption: { display: "flex", alignItems: "flex-start", gap: 10, padding: 12, background: cardBg, borderRadius: 8, border: `1px solid ${cardBorder}`, cursor: "pointer" },
      radio: { marginTop: 2 },
      roleName: { fontWeight: 600, marginBottom: 4, textTransform: "capitalize", color: textColor },
      roleDesc: { color: textSecondary, fontSize: 13 },
      
      // Info box
      infoBox: { background: cardBg, borderRadius: 8, padding: 16, marginBottom: 20, fontSize: 14, color: textSecondary, border: `1px solid ${cardBorder}` },
      copyBox: { display: "flex", gap: 8 },
      copyInput: { flex: 1, padding: "10px 12px", background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 8, color: textColor, fontSize: 13 },
      copyBtn: { padding: "10px 16px", border: "none", borderRadius: 8, color: buttonText, cursor: "pointer", fontSize: 13, background: buttonBg },
      
      // Modal actions
      modalActions: { display: "flex", gap: 12, marginTop: 24 },
      cancelBtn: { flex: 1, padding: 10, background: "transparent", border: `1px solid ${cardBorder}`, borderRadius: 8, color: textSecondary, cursor: "pointer", fontSize: 14 },
      saveBtn: { flex: 1, padding: 10, background: buttonBg, border: "none", borderRadius: 8, color: buttonText, cursor: "pointer", fontSize: 14, fontWeight: 500 },
      
      // Delete warning
      deleteWarning: { padding: 12, background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.2)", borderRadius: 8, fontSize: 13, color: "#fca5a5", marginTop: 8 },
      deleteSaveBtn: { flex: 1, padding: 10, background: "#ef4444", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 500 },
      
      // Expose values for inline use
      textColor, textSecondary, cardBg, cardBorder, inputBg, inputBorder, buttonBg, buttonText, isLightBackground, primaryColor
    };
  }, [branding, primaryColor, backgroundColor, isLightBackground]);

  // Dashboard URL
  const dashboardUrl = tenantKeyFromSubdomain ? "/admin" : `/client/${tenantKey}`;

  return (
    <div style={dynamicStyles.container}>
      {/* Header */}
      <div style={dynamicStyles.header}>
        <div>
          <h1 style={dynamicStyles.title}>{companyName}</h1>
          <span style={dynamicStyles.subtitle}>User Management</span>
        </div>
        <button onClick={() => navigate(dashboardUrl)} style={dynamicStyles.backButton}>
          ← Dashboard
        </button>
      </div>

      <div style={dynamicStyles.content}>
        <div style={dynamicStyles.pageHeader}>
          <div>
            <h2 style={dynamicStyles.pageTitle}>Team Members</h2>
            <p style={dynamicStyles.pageSubtitle}>
              Manage who has access to your interview platform
            </p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            style={dynamicStyles.button}
          >
            + Add User
          </button>
        </div>

        {/* Search */}
        <div style={dynamicStyles.filters}>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ ...dynamicStyles.searchInput, background: dynamicStyles.inputBg, border: `1px solid ${dynamicStyles.inputBorder}`, color: dynamicStyles.textColor }}
          />
          <button onClick={loadData} style={{ ...dynamicStyles.refreshBtn, background: dynamicStyles.cardBg, border: `1px solid ${dynamicStyles.cardBorder}`, color: dynamicStyles.textColor }}>
            ↻ Refresh
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={dynamicStyles.error}>
            {error}
            <button onClick={() => setError(null)} style={dynamicStyles.errorDismiss}>✕</button>
          </div>
        )}

        {/* Users */}
        {loading ? (
          <div style={dynamicStyles.loading}>Loading team members...</div>
        ) : (
          <div style={dynamicStyles.tableContainer}>
            <table style={dynamicStyles.table}>
              <thead>
                <tr>
                  <th style={dynamicStyles.th}>User</th>
                  <th style={dynamicStyles.th}>Email</th>
                  <th style={dynamicStyles.th}>Provider</th>
                  <th style={dynamicStyles.th}>Status</th>
                  <th style={dynamicStyles.th}>Joined</th>
                  <th style={dynamicStyles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={dynamicStyles.emptyCell}>
                      <p style={{ margin: "0 0 8px 0", fontSize: "16px" }}>No team members yet</p>
                      <p style={{ margin: 0, color: "#6b7280", fontSize: "14px" }}>
                        Add your team to start collaborating
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map(u => (
                    <tr key={u.id} style={{
                      ...dynamicStyles.tr,
                      opacity: u.is_disabled ? 0.5 : 1
                    }}>
                      <td style={dynamicStyles.td}>
                        <div style={dynamicStyles.userCell}>
                          {u.picture_url ? (
                            <img src={u.picture_url} alt="" style={dynamicStyles.avatar} />
                          ) : (
                            <div style={dynamicStyles.avatarPlaceholder}>
                              {u.name?.[0] || u.email?.[0] || "?"}
                            </div>
                          )}
                          <span>{u.name || "—"}</span>
                        </div>
                      </td>
                      <td style={dynamicStyles.td}>{u.email}</td>
                      <td style={dynamicStyles.td}>
                        <span style={{
                          ...dynamicStyles.providerBadge,
                          background: u.oauth_provider === "google" ? "rgba(66, 133, 244, 0.2)" : 
                                     u.oauth_provider === "microsoft" ? "rgba(0, 120, 212, 0.2)" :
                                     "rgba(107, 114, 128, 0.2)",
                          color: u.oauth_provider === "google" ? "#4285f4" : 
                                 u.oauth_provider === "microsoft" ? "#0078d4" :
                                 "#9ca3af"
                        }}>
                          {u.oauth_provider || "email"}
                        </span>
                      </td>
                      <td style={dynamicStyles.td}>
                        <span style={{
                          ...dynamicStyles.statusBadge,
                          background: u.is_disabled ? "rgba(239, 68, 68, 0.2)" : "rgba(34, 197, 94, 0.2)",
                          color: u.is_disabled ? "#ef4444" : "#22c55e"
                        }}>
                          {u.is_disabled ? "Disabled" : "Active"}
                        </span>
                      </td>
                      <td style={dynamicStyles.td}>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td style={dynamicStyles.td}>
                        <div style={dynamicStyles.actions}>
                          <button
                            onClick={() => {
                              setSelectedUser(u);
                              setModalRole("recruiter");
                              setShowRoleModal(true);
                            }}
                            style={dynamicStyles.actionBtn}
                            title="Assign Role"
                          >
                            Role
                          </button>
                          <button
                            onClick={() => handleToggleUserStatus(u)}
                            style={{
                              ...dynamicStyles.actionBtn,
                              ...(u.is_disabled ? dynamicStyles.enableBtn : dynamicStyles.disableBtn)
                            }}
                            title={u.is_disabled ? "Enable User" : "Disable User"}
                          >
                            {u.is_disabled ? "Enable" : "Disable"}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(u);
                              setShowDeleteModal(true);
                            }}
                            style={{ ...dynamicStyles.actionBtn, ...dynamicStyles.deleteBtn }}
                            title="Remove User"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Stats */}
        <div style={dynamicStyles.stats}>
          Total Members: {users.length}
          {users.filter(u => u.is_disabled).length > 0 && (
            <span style={{ marginLeft: 16, color: "#ef4444" }}>
              • {users.filter(u => u.is_disabled).length} disabled
            </span>
          )}
        </div>
      </div>

      {/* Assign Role Modal */}
      {showRoleModal && selectedUser && (
        <div style={dynamicStyles.modalOverlay} onClick={() => setShowRoleModal(false)}>
          <div style={dynamicStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={dynamicStyles.modalTitle}>Assign Role</h3>
            <p style={dynamicStyles.modalSubtitle}>
              Assigning role to: <strong>{selectedUser.email}</strong>
            </p>
            
            <div style={dynamicStyles.formGroup}>
              <label style={dynamicStyles.label}>Role</label>
              <div style={dynamicStyles.roleOptions}>
                {availableRoles.map(r => (
                  <label key={r} style={{
                    ...dynamicStyles.roleOption,
                    borderColor: modalRole === r ? dynamicStyles.buttonBg : dynamicStyles.cardBorder
                  }}>
                    <input
                      type="radio"
                      name="role"
                      value={r}
                      checked={modalRole === r}
                      onChange={() => setModalRole(r)}
                      style={dynamicStyles.radio}
                    />
                    <div>
                      <div style={dynamicStyles.roleName}>{r}</div>
                      <div style={dynamicStyles.roleDesc}>
                        {r === "admin" 
                          ? "Can manage settings, users, and all jobs"
                          : "Can create jobs and manage candidates"}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            
            <div style={dynamicStyles.modalActions}>
              <button
                onClick={() => setShowRoleModal(false)}
                style={dynamicStyles.cancelBtn}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignRole}
                style={{ ...dynamicStyles.saveBtn, background: dynamicStyles.buttonBg, color: dynamicStyles.buttonText }}
                disabled={saving}
              >
                {saving ? "Saving..." : "Assign Role"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite/Add User Modal */}
      {showInviteModal && (
        <div style={dynamicStyles.modalOverlay} onClick={() => setShowInviteModal(false)}>
          <div style={dynamicStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={dynamicStyles.modalTitle}>Add Team Member</h3>
            
            <div style={dynamicStyles.infoBox}>
              <p style={{ margin: "0 0 12px 0" }}>
                <strong>How to add users:</strong>
              </p>
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                <li style={{ marginBottom: 8 }}>Share the login link below with your team member</li>
                <li style={{ marginBottom: 8 }}>Have them sign in with Google or Microsoft</li>
                <li>Once signed in, they'll appear here and you can assign their role</li>
              </ol>
            </div>
            
            <div style={dynamicStyles.formGroup}>
              <label style={dynamicStyles.label}>Login Link</label>
              <div style={dynamicStyles.copyBox}>
                <input
                  type="text"
                  value={`https://${tenantKey}.invenire.app/login`}
                  readOnly
                  style={dynamicStyles.copyInput}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`https://${tenantKey}.invenire.app/login`);
                    alert("Link copied!");
                  }}
                  style={{ ...dynamicStyles.copyBtn, background: dynamicStyles.buttonBg, color: dynamicStyles.buttonText }}
                >
                  Copy
                </button>
              </div>
            </div>
            
            <div style={dynamicStyles.modalActions}>
              <button
                onClick={() => setShowInviteModal(false)}
                style={dynamicStyles.cancelBtn}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete/Remove User Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div style={dynamicStyles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div style={dynamicStyles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ ...dynamicStyles.modalTitle, color: "#ef4444" }}>Remove User</h3>
            <p style={dynamicStyles.modalSubtitle}>
              Are you sure you want to remove <strong>{selectedUser.email}</strong> from {tenant?.name}?
            </p>
            
            <div style={dynamicStyles.deleteWarning}>
              ⚠️ This will revoke their access to this organization. They will no longer be able to sign in or view any data.
            </div>
            
            <div style={dynamicStyles.modalActions}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={dynamicStyles.cancelBtn}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                style={dynamicStyles.deleteSaveBtn}
                disabled={saving}
              >
                {saving ? "Removing..." : "Remove User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
